import {
  ActionButtonFormElement,
  ActionButtonType,
  AnswerReviewFormElement,
  BooleanDisplayFormat,
  BooleanFormElement,
  CascaderFormElement,
  ChangeStatusButtonFormElement,
  CheckboxesFormElement,
  CheckboxOptionsDisplayFormat,
  DateFormElement,
  DiscussionFormElement,
  DividerFormElement,
  EntityFormElement,
  FileUploadFormElement,
  FormElementBase,
  FormElementType,
  FormResponseStatus,
  FormTextFormElement,
  HeadingFormElement,
  LinkedAnswerFormElement,
  MailingAddressFormElement,
  MoneyFormElement,
  NameStampFormElement,
  NumberFormElement,
  RadioButtonsDisplayFormat,
  RadioButtonsFormElement,
  RepeaterFormElement,
  RichTextFormElement,
  RichTextPluginOptions,
  RichTextToolbarElement,
  SectionFormElement,
  SecurityGroupFormElement,
  SelectListFormElement,
  SimpleTextFormElement,
  SysOrgRoleName,
  Thickness,
  UserSelectFormElement,
  WorkflowAssociationFormElement,
  ActivityDateFormElement,
  ApplicationStatusSelectFormElement,
  PrimaryContactSelectFormElement,
  EnvironmentIndicatorFormElement,
  SpecialQuestion,
} from "~/api";

export type FormElementDefault<T> = Omit<
  T,
  "guid" | "parentGuid" | "formDefinitionGuid"
> & {
  icon?: string | null;
};
export type PropertiesRequired<T extends { properties?: any }> =
  FormElementDefault<T> & {
    properties: Required<T["properties"]>;
  };

// IMPORTANT: Make sure the 'type' field is at the top of the object
// for more info ask Ethan

/*

The sort order of properties in this file is used when dragging a new item onto a form.

The sort order of properties defined in the c# code for each element is used at all other times.

Ensure that they are the same!

*/

const defaultBaseProperties = {
  title: null,
  indentLevel: 0,
  specialQuestion: null as any, // Fixed: use null instead of NONE to properly indicate no special question
  comment: null,
  showCommentBelow: false,
};

const defaultQuestionBaseProperties = {
  ...defaultBaseProperties,
  isOptional: false,
  showOptionality: false,
  isReadOnly: false,
  sensitivity: {},
  saveToTableName: null,
  saveToColumnName: null,
  saveToTargetingMode: undefined as any,
  saveAnsweringUserGuid: null,
  getFromLinkType: null,
  carryForwardMode: null as any,
  carryForward: false,
  excludeFromMainPdf: false,
  customPdfCodes: [],
  makeAvailableToOtherForms: false,
};

const defaultBoolean: PropertiesRequired<BooleanFormElement> = {
  type: FormElementType.BOOLEAN,
  label: "Yes/No",
  icon: "check",
  questionCode: "YesNo",
  events: [],
  properties: {
    displayFormat: BooleanDisplayFormat.YES_NO,
    noDeselect: false,
    ...defaultQuestionBaseProperties,
  },
};

const defaultCascader: PropertiesRequired<CascaderFormElement> = {
  type: FormElementType.CASCADER,
  label: "Cascader",
  icon: "form-dropdown",
  questionCode: "Select",
  events: [],
  properties: {
    placeholder: null,
    cascaderOptions: [
      {
        value: "Value1",
        label: "Option 1",
        children: [{ value: "1", label: "1", children: [] }],
      },
      {
        value: "Value2",
        label: "Option 2",
        children: [{ value: "2", label: "2", children: [] }],
      },
      {
        value: "Value3",
        label: "Option 3",
        children: [{ value: "3", label: "3", children: [] }],
      },
    ],
    noneText: null,
    ...defaultQuestionBaseProperties,
  },
};

const defaultCheckboxes: PropertiesRequired<CheckboxesFormElement> = {
  type: FormElementType.CHECKBOXES,
  label: "Checkboxes",
  icon: "checkbox-multiple-marked-outline",
  questionCode: "Checkboxes",
  events: [],
  properties: {
    checkboxOptionsDisplayFormat: CheckboxOptionsDisplayFormat.DEFAULT,
    checkboxOptions: [
      { value: "Value1", label: "Option 1" },
      { value: "Value2", label: "Option 2" },
      { value: "Value3", label: "Option 3" },
    ],
    hideHeader: null,
    ...defaultQuestionBaseProperties,
  },
};

const defaultDate: PropertiesRequired<DateFormElement> = {
  type: FormElementType.DATE,
  label: "Date",
  icon: "calendar-blank-outline",
  questionCode: "Date",
  events: [],
  properties: {
    placeholder: null,
    enableTime: false,
    minDateCode: null,
    maxDateCode: null,
    startDateCode: null,
    ...defaultQuestionBaseProperties,
  },
};

const defaultEntity: PropertiesRequired<EntityFormElement> = {
  type: FormElementType.ENTITY,
  label: "Entity",
  icon: "account",
  questionCode: "Entity",
  events: [],
  properties: {
    entityDefinitionGuid: "",
    ...defaultQuestionBaseProperties,
  },
};

const defaultFileUpload: PropertiesRequired<FileUploadFormElement> = {
  type: FormElementType.FILE_UPLOAD,
  label: "File Upload",
  icon: "file-upload-outline",
  questionCode: "Upload",
  events: [],
  properties: {
    noFilesMessage: null,
    maxFiles: 1,
    compressImagesForNormalPrinting: true,
    accept: "image/*,application/pdf",
    ...defaultQuestionBaseProperties,
  },
};

const defaultActionButton: PropertiesRequired<ActionButtonFormElement> = {
  type: FormElementType.ACTION_BUTTON,
  label: "Action",
  icon: "check-circle-outline",
  questionCode: "Action",
  events: [],
  properties: {
    actionButtonText: "Action",
    actionToTake: ActionButtonType.GET_PDF,
    code: null,
    ...defaultQuestionBaseProperties,
  },
};

const defaultAnswerReview: PropertiesRequired<AnswerReviewFormElement> = {
  type: FormElementType.ANSWER_REVIEW,
  label: "Review Answers",
  icon: "format-align-left",
  questionCode: "ReviewAnswers",
  events: [],
  properties: {
    changeToStatus: FormResponseStatus.NEW,
    changeWorkflowStatusTo: null,
    changeBackToStatus: FormResponseStatus.DRAFT,
    changeWorkflowStatusBackTo: null,
    questionsRemainingText:
      "There are missing answers. Please click the button below to see what is missing.",
    noQuestionsRemainingText:
      "You have completed all the required questions. When you are ready to submit, please confirm below.",
    statusUpdatedText: "All required questions have been answered.",
    reviewButtonText: "Review Answers",
    confirmButtonText: "Confirm",
    hideConfirmButton: false,
    ...defaultQuestionBaseProperties,
  },
};

const defaultDivider: FormElementDefault<DividerFormElement> = {
  type: FormElementType.DIVIDER,
  label: "Divider",
  icon: "minus",
  questionCode: "Divider",
  events: [],
  properties: {
    displayLine: false,
    thickness: Thickness.MEDIUM,
    ...defaultBaseProperties,
  },
};

const defaultDiscussionThread: FormElementDefault<DiscussionFormElement> = {
  type: FormElementType.DISCUSSION,
  label: "Discussion Thread",
  icon: "forum-outline",
  questionCode: "Discussion",
  events: [],
  properties: {
    placeholder: "Type your comment here",
    saveButtonText: "Post",
    cancelButtonText: "Cancel",
    startCollapsed: false,
    colors: [
      "#EF9A9A", // light red
      "#A5D6A7", // light green
      "#90CAF9", // light blue
      "#FFD54F", // yellow
      "#CE93D8", // lavender
    ],
    ...defaultBaseProperties,
  },
};

const defaultChangeStatusButton: FormElementDefault<ChangeStatusButtonFormElement> =
  {
    type: FormElementType.CHANGE_STATUS_BUTTON,
    label: "Change Status",
    icon: "list-status",
    questionCode: "ChangeStatus",
    events: [],
    properties: {
      changeWorkflowStatusTo: null,
      buttonText: "Submit",
      // // @ts-expect-error: set to null so the designer can choose
      // showWhenStatus: null,
      // // @ts-expect-error: set to null so the designer can choose
      // hideWhenStatus: null,
      // // @ts-expect-error: set to null so the designer can choose
      // changeToStatus: null,
      disableIfUnansweredQuestions: true,
      customActionCode: null,
      ...defaultBaseProperties,
    },
  };

const defaultMailingAddress: PropertiesRequired<MailingAddressFormElement> = {
  type: FormElementType.MAILING_ADDRESS,
  label: "Mailing Address",
  icon: "map-marker-outline",
  questionCode: "Address",
  events: [],
  properties: {
    generic: false,
    ...defaultQuestionBaseProperties,
  },
};

const defaultMoney: PropertiesRequired<MoneyFormElement> = {
  type: FormElementType.MONEY,
  label: "Money",
  icon: "currency-usd",
  questionCode: "Money",
  events: [],
  properties: {
    currency: "$",
    min: 0,
    max: null,
    enableCopy: true,
    ...defaultQuestionBaseProperties,
  },
};

const defaultNumber: PropertiesRequired<NumberFormElement> = {
  type: FormElementType.NUMBER,
  label: "Number",
  icon: "numeric",
  questionCode: "Number",
  events: [],
  properties: {
    placeholder: null,
    min: null,
    max: null,
    precision: null,
    enableCopy: true,
    showControls: false,
    ...defaultQuestionBaseProperties,
  },
};

const defaultRadioButtons: PropertiesRequired<RadioButtonsFormElement> = {
  type: FormElementType.RADIO_BUTTONS,
  label: "Radio Buttons",
  icon: "radiobox-marked",
  questionCode: "Radio",
  events: [],
  properties: {
    displayFormat: RadioButtonsDisplayFormat.NORMAL,
    radioButtonOptions: [
      { value: "Value1", label: "Option 1" },
      { value: "Value2", label: "Option 2" },
      { value: "Value3", label: "Option 3" },
    ],
    ...defaultQuestionBaseProperties,
  },
};

const defaultRepeater: PropertiesRequired<RepeaterFormElement> = {
  type: FormElementType.REPEATER,
  label: "Repeater",
  icon: "repeat",
  questionCode: "Repeater",
  events: [],
  properties: {
    itemTitle: "New Item",
    addButtonText: "Add New Item",
    removeButtonText: "Remove",
    noItemsText: "Click below to add a New Item",
    showNumber: false,
    defaultNumberToAdd: 1,
    ...defaultQuestionBaseProperties,
  },
};

const defaultRichText: PropertiesRequired<RichTextFormElement> = {
  type: FormElementType.RICH_TEXT,
  label: "Rich Text",
  icon: "format-align-left",
  questionCode: "RichText",
  events: [],
  properties: {
    placeholder: null,
    // set default toolbar elements for a basic rich text editor
    toolbarElements: "undo,redo,bold,italic,bullist,numlist".split(
      ",",
    ) as RichTextToolbarElement[],
    pluginOptions: Object.values(
      RichTextPluginOptions,
    ) as RichTextPluginOptions[],
    maxLength: null,
    maxWordCount: null,
    ...defaultQuestionBaseProperties,
  },
};

const defaultSecurityGroup: PropertiesRequired<SecurityGroupFormElement> = {
  type: FormElementType.SECURITY_GROUP,
  label: "Security Group",
  icon: "shield-account",
  questionCode: "SecurityGroup",
  events: [],
  properties: {
    allowedSecurityGroups: [],
    ...defaultQuestionBaseProperties,
  },
};

const defaultSelectList: PropertiesRequired<SelectListFormElement> = {
  type: FormElementType.SELECT_LIST,
  label: "Select List",
  icon: "form-dropdown",
  questionCode: "Select",
  events: [],
  properties: {
    placeholder: null,
    selectListOptions: [
      { value: "Value1", label: "Option 1" },
      { value: "Value2", label: "Option 2" },
      { value: "Value3", label: "Option 3" },
    ],
    noneText: null,
    ...defaultQuestionBaseProperties,
  },
};

const defaultUserSelect: PropertiesRequired<UserSelectFormElement> = {
  type: FormElementType.USER_SELECT,
  label: "User Select",
  icon: "account-outline",
  questionCode: "UserSelect",
  events: [],
  properties: {
    roleName: SysOrgRoleName.MEMBER,
    placeholder: null,
    ...defaultQuestionBaseProperties,
  },
};

const defaultApplicationStatusSelect: PropertiesRequired<ApplicationStatusSelectFormElement> =
  {
    type: FormElementType.APPLICATION_STATUS_SELECT,
    label: "Application Status",
    icon: "list-status",
    questionCode: "ApplicationStatus",
    events: [],
    properties: {
      placeholder: null,
      selectListOptions: [],
      noneText: null,
      ...defaultQuestionBaseProperties,
    },
  };

const defaultPrimaryContactSelect: PropertiesRequired<PrimaryContactSelectFormElement> =
  {
    type: FormElementType.PRIMARY_CONTACT_SELECT,
    label: "Primary Contact",
    icon: "star-circle-outline",
    questionCode: "PrimaryContact",
    events: [],
    properties: {
      placeholder: null,
      ...defaultQuestionBaseProperties,
      // Pre-wire the starring behavior so designers don't need to set it manually
      specialQuestion: SpecialQuestion.SET_PRIMARY_CONTACT,
    },
  };

const defaultSimpleText: PropertiesRequired<SimpleTextFormElement> = {
  type: FormElementType.SIMPLE_TEXT,
  label: "Simple Text",
  icon: "text-short",
  questionCode: "Text",
  events: [],
  properties: {
    placeholder: null,
    autocomplete: "off",
    validation: null,
    validationType: null!,
    maxLength: null,
    maxWordCount: null,
    isMultiline: false,
    actionToTake: ActionButtonType.NONE,
    code: null,
    defaultAnswer: null,
    ...defaultQuestionBaseProperties,
  },
};

const defaultNameStamp: PropertiesRequired<NameStampFormElement> = {
  type: FormElementType.NAME_STAMP,
  label: "Name Stamp",
  icon: "stamper",
  questionCode: "NameStamp",
  events: [],
  properties: {
    nameStampButtonText: "Approve",
    nameStampRevokeButtonText: "Cancel",
    changeToStatus: FormResponseStatus.READY_FOR_SUBMISSION,
    changeWorkflowStatusTo: null,
    isRevokable: true,
    changeBackToStatus: FormResponseStatus.DRAFT,
    changeWorkflowStatusBackTo: null,
    actionToTake: ActionButtonType.NONE,
    code: null,
    leftAligned: false,
    isRevokableByAnyUser: false,
    allowMultipleClicks: false,
    disableIfUnansweredQuestions: true,
    ...defaultQuestionBaseProperties,
  },
};

const defaultActivityDate: PropertiesRequired<ActivityDateFormElement> = {
  type: FormElementType.ACTIVITY_DATE,
  label: "Activity Date",
  icon: "calendar-clock-outline",
  questionCode: "ActivityDate",
  events: [],
  properties: {
    addPageBreakBefore: false,
    // designer-controlled ActivityDate properties
    allowPastDates: null,
    dateShortcuts: null,
    remindersEnabled: null,
    notifyTarget: null as any,
    notifyUserSelectElementGuid: null,
    ...defaultQuestionBaseProperties,
  },
};

const defaultEnvironmentIndicator: FormElementDefault<EnvironmentIndicatorFormElement> =
  {
    type: FormElementType.ENVIRONMENT_INDICATOR,
    label: "Environment",
    icon: "server-network",
    questionCode: "EnvironmentIndicator",
    events: [],
    properties: {
      hideWhenLive: false,
      prefix: null,
      seedEnvironmentIntoQuestionCode: null,
      ...defaultQuestionBaseProperties,
      // The value is stamped by the component on first open, never typed by a user.
      isReadOnly: true,
    },
  };

export const defaultFormComponents: FormElementDefault<FormElementBase>[] = [
  // this determines the order they show in the editor - sort alphabetically
  defaultActivityDate,
  defaultBoolean,
  defaultCascader,
  defaultCheckboxes,
  defaultDate,
  defaultDiscussionThread,
  defaultSelectList,
  defaultEntity,
  defaultFileUpload,
  defaultMailingAddress,
  defaultMoney,
  defaultNumber,
  defaultRadioButtons,
  defaultRichText,
  defaultSecurityGroup,
  defaultSimpleText,
  defaultUserSelect,
];

const defaultSection: FormElementDefault<SectionFormElement> = {
  type: FormElementType.SECTION,
  label: "New Section",
  icon: "format-list-group",
  questionCode: "Section",
  events: [],
  properties: defaultBaseProperties,
};

const defaultFormText: PropertiesRequired<FormTextFormElement> = {
  type: FormElementType.FORM_TEXT,
  label: "Text Block",
  icon: "text-long",
  questionCode: "FormText",
  events: [],
  properties: {
    content: "",
    isHeading: null,
    saveAsDisplayed: false,
    isHidden: false,
    ...defaultQuestionBaseProperties,
  },
};

const defaultHeading: FormElementDefault<HeadingFormElement> = {
  type: FormElementType.HEADING,
  label: "Heading",
  icon: "format-header-1",
  questionCode: "Page",
  events: [],
  properties: {
    headingLevel: 0,
    addPageBreakBefore: false,
    ...defaultBaseProperties,
  },
};

const defaultLinkedAnswer: PropertiesRequired<LinkedAnswerFormElement> = {
  type: FormElementType.LINKED_ANSWER,
  label: "Linked Answer",
  icon: "link",
  questionCode: "LinkedAnswer",
  events: [],
  properties: {
    linkedFormDefinitionGuid: "",
    linkedFormElementGuid: "",
    ...defaultQuestionBaseProperties,
  },
};
export const structureElements: FormElementDefault<FormElementBase>[] = [
  defaultSection,
  defaultDivider,
  defaultRepeater,
  defaultLinkedAnswer,
  defaultEnvironmentIndicator,
];

export const contentElements: FormElementDefault<FormElementBase>[] = [
  defaultHeading,
  defaultFormText,
];

const defaultWorkflowAssociation: FormElementDefault<WorkflowAssociationFormElement> =
  {
    type: FormElementType.WORKFLOW_ASSOCIATION,
    label: "Associations",
    icon: "link-variant",
    questionCode: "Associations",
    events: [],
    properties: {
      ...defaultBaseProperties,
    },
  };

export const actionElements: FormElementDefault<FormElementBase>[] = [
  defaultNameStamp,
  defaultChangeStatusButton,
  defaultActionButton,
  defaultAnswerReview,
];

/**
 * Elements custom to the Housing module — shown in their own palette section.
 * WorkflowAssociation renders HousingContactActivityAssociations under the hood.
 */
export const housingElements: FormElementDefault<FormElementBase>[] = [
  defaultApplicationStatusSelect,
  defaultPrimaryContactSelect,
  defaultWorkflowAssociation,
];
